import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ArrowRight, Cpu, Sparkles } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section id="top" className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden" data-testid="hero-section">
      {/* Background gradient orbs */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 -left-32 w-[480px] h-[480px] rounded-full bg-bicenter-cyan/20 blur-[140px] animate-float-slow"></div>
        <div className="absolute bottom-1/4 -right-20 w-[520px] h-[520px] rounded-full bg-bicenter-violet/25 blur-[160px] animate-float-slower"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[300px] h-[300px] rounded-full bg-bicenter-magenta/15 blur-[120px]"></div>
      </div>
      <div className="absolute inset-0 bg-grid opacity-30 -z-10"></div>
      <div className="absolute inset-0 bg-noise opacity-[0.04] mix-blend-overlay pointer-events-none -z-10"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm mb-8" data-testid="hero-badge">
              <Sparkles size={14} className="text-bicenter-cyan" />
              <span className="text-xs uppercase tracking-[0.32em] text-zinc-300">{t.hero.badge}</span>
            </div>

            <h1 className="font-display font-light tracking-tighter text-white leading-[0.95]">
              <span className="block text-5xl md:text-7xl lg:text-8xl">{t.hero.title_1}</span>
              <span className="block text-5xl md:text-7xl lg:text-8xl text-gradient-mix font-medium">
                {t.hero.title_2}
              </span>
              <span className="block text-5xl md:text-7xl lg:text-8xl">{t.hero.title_3}</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-zinc-300 max-w-xl font-body leading-relaxed" data-testid="hero-subtitle">
              {t.hero.subtitle}
            </p>
            <p className="mt-3 text-sm uppercase tracking-[0.4em] text-zinc-500 font-body">
              {t.hero.tagline}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#demo"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white px-8 py-4 rounded-full font-medium hover:shadow-[0_0_40px_rgba(0,169,255,0.5)] transition-all duration-300"
              >
                {t.hero.cta_primary}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#solutions"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-white/15 text-white px-8 py-4 rounded-full font-medium hover:bg-white/5 hover:border-white/30 transition-all duration-300"
              >
                {t.hero.cta_secondary}
              </a>
              <a
                href="#activate"
                data-testid="hero-cta-tertiary"
                className="group inline-flex items-center gap-2 bg-gradient-to-r from-bicenter-violet to-bicenter-magenta text-white px-8 py-4 rounded-full font-medium hover:shadow-[0_0_40px_rgba(231,0,255,0.5)] transition-all duration-300"
              >
                {t.hero.cta_tertiary}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-3 gap-4 max-w-2xl">
              {[
                { label: t.hero.stat_1, value: t.hero.stat_1_v },
                { label: t.hero.stat_2, value: t.hero.stat_2_v },
                { label: t.hero.stat_3, value: t.hero.stat_3_v },
              ].map((s, i) => (
                <div key={i} className="border-l border-white/10 pl-4" data-testid={`hero-stat-${i}`}>
                  <p className="font-display text-2xl md:text-3xl font-light text-white">{s.value}</p>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Visual side */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square max-w-[520px] mx-auto">
              {/* Concentric rings */}
              <div className="absolute inset-0 rounded-full border border-white/5 animate-pulse-soft"></div>
              <div className="absolute inset-8 rounded-full border border-white/10"></div>
              <div className="absolute inset-16 rounded-full border border-bicenter-cyan/20"></div>
              <div className="absolute inset-24 rounded-full border border-bicenter-violet/20"></div>

              {/* Gradient glow center */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-2/3 h-2/3 rounded-full bg-gradient-to-br from-bicenter-cyan/30 via-bicenter-violet/30 to-bicenter-magenta/30 blur-3xl"></div>
              </div>

              {/* Center logo */}
              <div className="absolute inset-0 flex items-center justify-center">
                <LogoMark className="h-40 w-40 md:h-52 md:w-52" />
              </div>

              {/* Floating chips */}
              <div className="absolute top-4 right-6 backdrop-blur-md bg-white/[0.04] border border-white/10 rounded-2xl p-4 max-w-[180px]">
                <div className="flex items-center gap-2">
                  <Cpu size={14} className="text-bicenter-cyan" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">AI Engine</span>
                </div>
                <p className="text-sm text-white mt-2 font-display font-medium">Decisions in &lt; 5 min</p>
              </div>
              <div className="absolute bottom-6 left-2 backdrop-blur-md bg-white/[0.04] border border-white/10 rounded-2xl p-4 max-w-[180px]">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-bicenter-magenta" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">LOB Suite</span>
                </div>
                <p className="text-sm text-white mt-2 font-display font-medium">4 modules · 1 OS</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
